/**
 * GameOpsTab — Platform Console · Rivalries-specific (game) operations.
 *
 * iter84.63 Phases B–D — health-first IA driven by the Game Health Registry
 * (/app/memory/game_health_registry.md, the source of truth):
 *   - Health — one posture tile per game_health domain (worst detector
 *              wins, same rollup math as value groups) + the capability
 *              registry with active/broken/deprecated badges.
 *   - Issues — flat queue of every open detector finding, each paired
 *              with its registered healer (inline API action or a jump
 *              to the owning tool).
 *   - Tools  — manual healers grouped by upstream (ESPN / API-Football /
 *              API-NFL / Slash Golf / Highlightly / cross-cutting), a
 *              Settings group (poller cadence per vendor — ALL poller
 *              cadence cards live here now, iter87.389 retired the old
 *              Templates → Pollers section entirely), and the Catalog
 *              group (template governance, migrated from /admin per
 *              Phase D).
 * Parity Sweep and Test Pollution are Release-Ops-only (deploy hygiene);
 * Release Ops shows a game-health posture chip instead of mirrored cards.
 */
import { useEffect, useState } from "react";
import { Gamepad2, RefreshCw, HeartPulse, ListChecks, Wrench, ArrowRight, BookOpenCheck, Stethoscope, FileJson, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useBrutalConfirm } from "@/components/BrutalConfirm";
import { SubTabs } from "@/components/site-admin/values-registry/RegistryShared";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { DriftWatchCardContent } from "@/components/site-admin/release-ops/DriftWatchCard";
import {
    OrphanPicksAuditCard,
    ScoringDriftHealCard,
} from "@/components/site-admin/ReleaseOpsAuditCards";
import SiteAdminCleanupCard from "@/components/SiteAdminCleanupCard";
import SiteAdminPollerCadenceCard from "@/components/SiteAdminPollerCadenceCard";
import SiteAdminRecomputeTeamStatsCard from "@/components/SiteAdminRecomputeTeamStatsCard";
import SiteAdminRepullUpstreamWinnersCard from "@/components/SiteAdminRepullUpstreamWinnersCard";
import SiteAdminTemplateVisibility from "@/components/SiteAdminTemplateVisibility";
import SlashGolfPollerCadenceCard from "@/components/site-admin/SlashGolfPollerCadenceCard";
import NflPollerCadenceCard from "@/components/site-admin/NflPollerCadenceCard";
import MlbPollerCadenceCard from "@/components/site-admin/MlbPollerCadenceCard";
import NbaPollerCadenceCard from "@/components/site-admin/NbaPollerCadenceCard";
import TemplateScoringCard from "@/components/site-admin/TemplateScoringCard";
import TemplateBindingsCard from "@/components/site-admin/TemplateBindingsCard";
import VendorDataCascadeCard from "@/components/site-admin/VendorDataCascadeCard";
import TemplatePotsCard from "@/components/site-admin/TemplatePotsCard";
import { WhatsThisButton } from "@/components/site-admin/release-ops/WhatsThisButton";
import VendorImpactCard from "@/components/site-admin/VendorImpactCard";
import FeatureCatalogCard from "@/components/site-admin/FeatureCatalogCard";
import GenerateUatCard from "@/components/site-admin/GenerateUatCard";

const POSTURE_PILL = {
    blocked: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    warn:    "bg-amber-500/15 text-amber-300 border-amber-500/40",
    notice:  "bg-sky-500/15 text-sky-300 border-sky-500/40",
    ok:      "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

const STATUS_PILL = {
    active:     "bg-emerald-900/40 text-emerald-200 border-emerald-700",
    broken:     "bg-rose-900/40 text-rose-200 border-rose-700",
    deprecated: "bg-zinc-800 text-zinc-500 border-zinc-700",
};

const KIND_PILL = "bg-zinc-800 text-zinc-300 border-zinc-700";

function Pill({ cls, children, testId }) {
    return (
        <span className={`pill text-[9px] font-bold uppercase border ${cls}`} data-testid={testId}>
            {children}
        </span>
    );
}

// ---------------------------------------------------------------- Health --

// iter87.114 — Capability-registry filter pill. Hoisted out of HealthView
// so React reconciliation doesn't destroy button state on every re-render.
function CapPill({ k, label, count, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`capability-filter-${k}`}
            aria-pressed={active}
            className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border transition-colors ${
                active
                    ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/60"
                    : "bg-transparent text-zinc-400 border-zinc-700 hover:text-zinc-200 hover:border-zinc-500"
            }`}
        >
            {label} <span className="ml-1 text-zinc-500 font-normal">({count})</span>
        </button>
    );
}

function HealthView({ summary, registry, onOpenIssues }) {
    // iter87.114 — Capability registry filter (Live | Deprecated | All).
    // Default hides deprecated so operators see the live surface first.
    const [capFilter, setCapFilter] = useState("live");
    const allRows = registry?.rows || [];
    const filteredRows = allRows.filter((r) => {
        if (capFilter === "live") return r.status !== "deprecated" && r.status !== "broken";
        if (capFilter === "deprecated") return r.status === "deprecated" || r.status === "broken";
        return true;
    });
    const liveCount = allRows.filter((r) => r.status !== "deprecated" && r.status !== "broken").length;
    const deprecatedCount = allRows.length - liveCount;

    return (
        <div className="space-y-4" data-testid="game-ops-health-view">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="game-ops-health-tiles">
                {(summary?.domains || []).map((d) => (
                    <div
                        key={d.domain}
                        className="brutal-card p-3 space-y-1"
                        data-testid={`game-ops-health-tile-${d.domain.split("/")[1]}`}
                    >
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-bold">{d.label}</div>
                        <Pill cls={POSTURE_PILL[d.posture] || POSTURE_PILL.warn} testId={`game-ops-health-posture-${d.domain.split("/")[1]}`}>
                            {d.posture}
                        </Pill>
                        <div className="text-[9px] text-zinc-600">
                            {d.findings.filter((f) => f.posture !== "ok").length || "no"} open finding(s)
                        </div>
                    </div>
                ))}
                {!summary && <div className="text-xs text-zinc-500 italic col-span-full">Running detectors…</div>}
            </div>

            {summary?.issue_count > 0 && (
                <button
                    type="button"
                    onClick={onOpenIssues}
                    data-testid="game-ops-health-open-issues-btn"
                    className="flex items-center gap-1.5 text-[10px] font-bold uppercase px-3 py-1.5 border border-amber-700 text-amber-300 rounded hover:bg-amber-900/20"
                >
                    {summary.issue_count} open issue(s) <ArrowRight className="w-3 h-3" />
                </button>
            )}

            <div className="brutal-card p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200">
                        Capability registry
                    </h3>
                    <div className="flex gap-1.5 flex-wrap" data-testid="capability-filter-toggle">
                        <CapPill k="live" label="Live" count={liveCount} active={capFilter === "live"} onClick={() => setCapFilter("live")} />
                        <CapPill k="deprecated" label="Deprecated" count={deprecatedCount} active={capFilter === "deprecated"} onClick={() => setCapFilter("deprecated")} />
                        <CapPill k="all" label="All" count={allRows.length} active={capFilter === "all"} onClick={() => setCapFilter("all")} />
                    </div>
                </div>
                <p className="text-[10px] text-zinc-500 mb-3">
                    Source of truth: <code>game_health_registry.md</code> (Docs → GAME_HEALTH_REGISTRY).
                    Broken or deprecated capabilities get badged here — never silently hidden.
                </p>
                <div className="space-y-1.5" data-testid="game-ops-registry-rows">
                    {filteredRows.map((r) => (
                        <div key={r.name} className="flex items-center gap-2 flex-wrap border border-zinc-800 rounded p-2 text-[11px] bg-black/30" data-testid={`game-ops-registry-row-${r.name}`}>
                            <code className="font-bold text-white">{r.name}</code>
                            <Pill cls={KIND_PILL}>{r.kind}</Pill>
                            <Pill cls={STATUS_PILL[r.status] || STATUS_PILL.active} testId={`game-ops-registry-status-${r.name}`}>{r.status}</Pill>
                            <span className="text-[9px] text-zinc-600">{r.domain} · {r.upstream} · {r.canonical_home}</span>
                        </div>
                    ))}
                    {filteredRows.length === 0 && (
                        <div className="text-[10px] text-zinc-500 italic px-2 py-3" data-testid="capability-filter-empty">
                            No capabilities match this filter.
                        </div>
                    )}
                </div>
            </div>

            <VendorImpactCard />
        </div>
    );
}

// ---------------------------------------------------------------- Issues --

function IssueRow({ issue, onHealed, onOpenTools }) {
    const [busy, setBusy] = useState(false);

    const runApiHeal = async () => {
        setBusy(true);
        try {
            if (issue.healer.action === "zero_entry_stage_reach") {
                await api.releaseOpsZeroEntryStageReachPoints();
            } else if (issue.healer.action === "bulk_recompute") {
                await api.driftWatchBulkRecompute({ dryRun: false });
            } else if (issue.healer.action === "heal_wcko_stages") {
                // iter84.72 — WCKO stage-tag heal (idempotent, audit-logged).
                await api.releaseOpsHealWckoStages();
            } else if (issue.healer.action === "heal_leaderboard_parity") {
                // iter86.19 — Recompute team stats for parity-drifted tournaments.
                await api.gameHealthHealLeaderboardParity();
            }
            toast.success(`${issue.label}: heal executed`);
            onHealed?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Heal failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex items-start gap-2 flex-wrap border border-zinc-800 rounded p-2.5 text-[11px] bg-black/30" data-testid={`game-ops-issue-${issue.id}`}>
            <Pill cls={POSTURE_PILL[issue.posture] || POSTURE_PILL.warn}>{issue.posture}</Pill>
            <div className="flex-1 min-w-0">
                <div className="font-bold text-white">{issue.label}
                    <span className="ml-2 text-[9px] font-normal text-zinc-600">{issue.domain_label}</span>
                </div>
                <p className="text-[10px] text-zinc-400 mt-0.5">{issue.detail}</p>
            </div>
            {issue.healer?.kind === "api" && (
                <button type="button" disabled={busy} onClick={runApiHeal} data-testid={`game-ops-issue-heal-${issue.id}`} className="brutal-btn text-[9px] py-1 px-2 bg-emerald-700 text-white">
                    {busy ? "Healing…" : issue.healer.label}
                </button>
            )}
            {issue.healer?.kind === "tools" && (
                <button type="button" onClick={() => onOpenTools(issue.healer.anchor)} data-testid={`game-ops-issue-open-tools-${issue.id}`} className="brutal-btn text-[9px] py-1 px-2">
                    {issue.healer.label}
                </button>
            )}
        </div>
    );
}

function IssuesView({ summary, reload, onOpenTools }) {
    const issues = summary?.issues || [];
    return (
        <div className="space-y-3" data-testid="game-ops-issues-view">
            {!summary && <div className="text-xs text-zinc-500 italic">Running detectors…</div>}
            {summary && issues.length === 0 && (
                <div className="border border-emerald-900/50 bg-emerald-950/10 rounded p-4 text-xs text-emerald-300" data-testid="game-ops-issues-empty">
                    All clear — every detector reports clean.
                </div>
            )}
            {issues.map((i) => (
                <IssueRow key={i.id} issue={i} onHealed={reload} onOpenTools={onOpenTools} />
            ))}
        </div>
    );
}

// ----------------------------------------------------------------- Tools --

function ToolGroup({ title, blurb, children, testId }) {
    return (
        <section className="space-y-3" data-testid={testId}>
            <header>
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-emerald-200">{title}</h3>
                <p className="text-[10px] text-zinc-500">{blurb}</p>
            </header>
            {children}
        </section>
    );
}

// iter86.30 — Healer Ledger: one row per registered healer with real run
// evidence. Replaces "hunt through Tools" as the way to see what fixes what.
function HealersView() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        api.gameHealthHealers()
            .then(setData)
            .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load"));
    }, []);
    if (error) return <div className="text-xs text-rose-300" data-testid="healers-error">{error}</div>;
    if (!data) return <div className="text-xs text-zinc-500" data-testid="healers-loading">Loading ledger…</div>;
    return (
        <div className="space-y-3" data-testid="game-ops-healers-view">
            <p className="text-[11px] text-zinc-500">
                Every registered healer, the detector/domain it serves, and its real run evidence.
                Healers without structured run logs yet are marked <span className="text-amber-300">not instrumented</span> — honest over pretty.
            </p>
            {data.healers.map((h) => (
                <div key={h.name} className="border border-zinc-800 rounded-lg p-3 bg-black/30" data-testid={`healer-row-${h.name}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <code className="text-xs font-bold text-white">{h.name}</code>
                        <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-600">{h.kind}</span>
                        <span className="pill text-[9px] font-bold uppercase border bg-cyan-900/40 text-cyan-200 border-cyan-700">{h.trigger}</span>
                        <span className="text-[9px] text-zinc-600">{h.domain} · {h.upstream} · blast {h.severity}</span>
                        <div className="ml-auto text-[10px]">
                            {h.instrumented ? (
                                <span className="text-emerald-300" data-testid={`healer-runs-${h.name}`}>
                                    {h.runs_7d} runs 7d · {h.runs_30d} runs 30d
                                    {typeof h.last_run === "string" && <span className="text-zinc-500"> · last {h.last_run.slice(0, 16).replace("T", " ")} ({h.last_source})</span>}
                                </span>
                            ) : (
                                <span className="text-amber-300" data-testid={`healer-uninstrumented-${h.name}`}>not instrumented</span>
                            )}
                        </div>
                    </div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">{h.description}</p>
                </div>
            ))}
        </div>
    );
}

// iter86.31.20 — Symptom-first Tools: operators pick by "what looks wrong",
// each tool carries applicability chips (format + vendor) and a plain-
// English "Use when" line. Golf / Season get explicit pointer cards.
const FORMAT_CHIPS = {
    soccer_ko: { label: "Soccer KO", cls: "bg-emerald-900/40 text-emerald-200 border-emerald-700" },
    soccer_season: { label: "Soccer Season", cls: "bg-sky-900/40 text-sky-200 border-sky-700" },
    golf: { label: "Golf", cls: "bg-lime-900/40 text-lime-200 border-lime-700" },
    football_season: { label: "Football Season", cls: "bg-amber-900/40 text-amber-200 border-amber-700" },
    odds_api: { label: "Odds API", cls: "bg-cyan-900/40 text-cyan-200 border-cyan-700" },
    mlb: { label: "MLB Playoffs", cls: "bg-blue-900/40 text-blue-200 border-blue-700" },
    nba: { label: "NBA Playoffs", cls: "bg-orange-900/40 text-orange-200 border-orange-700" },
    cfb_playoffs: { label: "CFB Playoffs", cls: "bg-orange-900/40 text-orange-200 border-orange-700" },
    any: { label: "Any format", cls: "bg-zinc-800 text-zinc-300 border-zinc-600" },
};
const VENDOR_CHIPS = {
    api_football: { label: "API-Football", cls: "bg-indigo-900/40 text-indigo-200 border-indigo-700" },
    the_odds_api: { label: "The Odds API", cls: "bg-cyan-900/40 text-cyan-200 border-cyan-700" },
    highlightly: { label: "Highlightly", cls: "bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700" },
    api_nfl: { label: "API-NFL", cls: "bg-amber-900/40 text-amber-200 border-amber-700" },
    slash_golf: { label: "Slash Golf", cls: "bg-lime-900/40 text-lime-200 border-lime-700" },
    cfbd: { label: "CFBD", cls: "bg-orange-900/40 text-orange-200 border-orange-700" },
    any: { label: "Any vendor", cls: "bg-zinc-800 text-zinc-300 border-zinc-600" },
};

// iter87.201 — Vendor grouping for Tools. Section headers organize by
// upstream (API-Football / The Odds API / cross-cutting / ESPN-retired)
// instead of by symptom, and every card now surfaces its registry
// description, whether an auto-healer already covers it, and real usage
// (7d/30d run counts) from the Healer Ledger — same data as the Healers
// sub-tab, folded into Tools so operators don't have to cross-reference.
const VENDOR_META = {
    "api-football": { label: "API-Football", cls: "bg-indigo-900/40 text-indigo-200 border-indigo-700" },
    "the-odds-api": { label: "The Odds API", cls: "bg-cyan-900/40 text-cyan-200 border-cyan-700" },
    highlightly: { label: "Highlightly (MLB/NBA)", cls: "bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700" },
    "api-nfl": { label: "API-NFL (Football)", cls: "bg-amber-900/40 text-amber-200 border-amber-700" },
    "slash-golf": { label: "Slash Golf", cls: "bg-lime-900/40 text-lime-200 border-lime-700" },
    cfbd: { label: "CollegeFootballData.com (CFBD)", cls: "bg-orange-900/40 text-orange-200 border-orange-700" },
    cross: { label: "Cross-cutting", cls: "bg-zinc-800 text-zinc-300 border-zinc-600" },
    retired: { label: "Retired — audit trail", cls: "bg-zinc-800 text-zinc-500 border-zinc-700" },
};

function autoHealerBadge(reg) {
    if (!reg) return null;
    const hasAuto = (reg.trigger || "").includes("auto");
    if (!hasAuto) return { text: "manual only", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" };
    if ((reg.kind || "").includes("healer")) return { text: "auto-heals + manual override", cls: "bg-emerald-900/30 text-emerald-300 border-emerald-700" };
    return { text: "auto-detects · manual heal", cls: "bg-sky-900/30 text-sky-300 border-sky-700" };
}

// iter87.201.1 — Data-flow line. Where the heal actually reads from and
// writes to, in plain source → destination language (not just "what
// problem it fixes"). Curated per tool since the registry description
// already covers the "why"; this covers the "where".
const TOOL_FLOW = {
    recompute_team_stats: "Recorded matches (source of truth) → rebuilds team W/D/L + stage_reached → re-applies sticky manual bracket edits on top.",
    repull_upstream_winners: "API-Football cached winner flags → re-pulled onto local match/team records → auto-recomputes team stats from the corrected winner.",
    odds_force_sync: "The Odds API bulk poller → pulls the latest spread line per active sport → writes it onto scheduled matches and backfills picks awaiting a first line.",
    odds_playoffs_force_sync: "The Odds API (spreads,totals) → NFL + CFB + MLS Playoffs active tournaments → writes spread (Asian handicap for MLS) + Over/Under lines onto scheduled matches for the Playoff Predictor.",
    scoring_drift_heal: "Recorded matches → recomputed crew scores → overwrites the drifted rows in the stored leaderboard.",
    drift_watch: "Scans the match collection per tournament for duplicate docs → read-only findings list; recompute is a separate manual step.",
    orphan_picks_audit: "Picks collection cross-checked against current teams/participants → deletes picks pointing at teams or players that no longer exist.",
    bulk_cleanup: "Scans leagues for no draft + no matches + ≤1 member + 30d+ old → deletes the abandoned rivalry doc and its dependents.",
    mark_game_complete: "scheduled_matches row → flips played=True (+ optional score/winner) → standings pick it up on the next Recompute Team Stats run.",
    mlb_poller_cadence: "Highlightly MLB Playoffs feed → poller re-syncs teams/series scores/bracket on cadence or Run Now → tournament's `mlb.rounds` + `last_synced_at` updated.",
    nba_poller_cadence: "Highlightly NBA Playoffs feed → poller re-syncs teams/series scores/bracket on cadence or Run Now → tournament's `nba.rounds` + `last_synced_at` updated.",
    api_football_poller_cadence: "API-Football cache TTLs (live/dark + lookahead) → applied on the poller's next wake → controls how fast soccer KO brackets pick up fresh scores. Settings only, no manual Run Now.",
    nfl_poller_cadence: "API-NFL (American Football) cache TTLs + live-window knobs → applied on the poller's next wake → controls how fast NFL season sync + weekly spreads refresh. Settings only, no manual Run Now.",
    golf_poller_cadence: "Slash Golf (RapidAPI) leaderboard/field cache TTLs → applied on the poller's next wake → controls how fast golf leaderboards refresh, with an automatic final-round (Sunday) boost. Settings only, no manual Run Now.",
    cfbd_resync: "CollegeFootballData.com CFP bracket endpoint → CFB Playoffs team pool (who's in the 12-team field + seed) → tournament's `cfb.rounds` team pool. Production-only, capped at 1 real call/day — this button re-serves the still-fresh cache (no-op, no extra call) if already refreshed today.",
};

function ToolMetaLine({ name, registryByName, healersByName }) {
    const reg = registryByName?.[name];
    const healer = healersByName?.[name];
    const badge = autoHealerBadge(reg);
    const flow = TOOL_FLOW[name];
    if (!reg && !healer && !flow) return null;
    return (
        <div className="mt-1.5 pt-1.5 border-t border-zinc-900 space-y-1" data-testid={`game-ops-tool-meta-${name}`}>
            <div className="flex items-center gap-2 flex-wrap text-[10px]">
                {reg?.description && <span className="text-zinc-500 flex-1 min-w-[220px]">{reg.description}</span>}
                {badge && <span className={`pill text-[9px] font-bold uppercase border shrink-0 ${badge.cls}`}>{badge.text}</span>}
                {healer && (
                    healer.instrumented ? (
                        <span className="text-zinc-400 shrink-0" data-testid={`game-ops-tool-usage-${name}`}>
                            {healer.runs_7d ? `${healer.runs_7d} runs · 7d` : "no runs · 7d"} — {healer.runs_30d ? `${healer.runs_30d} · 30d` : "0 · 30d"}
                            {healer.last_run ? ` — last ${healer.last_run.slice(0, 16).replace("T", " ")}` : ""}
                        </span>
                    ) : (
                        <span className="text-amber-300 shrink-0" data-testid={`game-ops-tool-usage-${name}`}>not instrumented — no run log yet</span>
                    )
                )}
                {!healer && reg && !(reg.kind || "").includes("healer") && (
                    <span className="text-zinc-600 shrink-0" data-testid={`game-ops-tool-usage-${name}`}>detector only — no manual healer run log</span>
                )}
            </div>
            {flow && (
                <div className="text-[10px] text-zinc-600" data-testid={`game-ops-tool-flow-${name}`}>
                    <span className="font-bold text-zinc-500">Data flow:</span> {flow}
                </div>
            )}
        </div>
    );
}

function ToolWrap({ formats, vendor, useWhen, testId, name, registryByName, healersByName, children }) {
    return (
        <div className="space-y-1.5 border border-zinc-800 rounded-lg p-3 bg-black/20" data-testid={testId}>
            <div className="flex items-start gap-1.5 flex-wrap">
                {formats.map((f) => (
                    <span key={f} className={`pill text-[9px] font-bold uppercase border shrink-0 ${FORMAT_CHIPS[f].cls}`}>{FORMAT_CHIPS[f].label}</span>
                ))}
                <span className={`pill text-[9px] font-bold uppercase border shrink-0 ${VENDOR_CHIPS[vendor].cls}`}>{VENDOR_CHIPS[vendor].label}</span>
                <span className="text-[10px] text-zinc-400 leading-relaxed">
                    <span className="font-bold text-zinc-200">Use when:</span> {useWhen}
                </span>
            </div>
            {children}
            <ToolMetaLine name={name} registryByName={registryByName} healersByName={healersByName} />
        </div>
    );
}

function VendorGroup({ vendorKey, blurb, count, testId, children }) {
    const meta = VENDOR_META[vendorKey];
    return (
        <section className="space-y-3" data-testid={testId}>
            <header className="flex items-center gap-2 flex-wrap">
                <span className={`pill text-[10px] font-bold uppercase border ${meta.cls}`}>{meta.label}</span>
                <span className="text-[10px] text-zinc-600">{count} tool{count === 1 ? "" : "s"}</span>
            </header>
            {blurb && <p className="text-[10px] text-zinc-500">{blurb}</p>}
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function RetiredRow({ r }) {
    return (
        <div className="flex items-start gap-2 flex-wrap border border-zinc-800 rounded p-2.5 text-[11px] bg-black/10" data-testid={`game-ops-tools-retired-${r.name}`}>
            <code className="font-bold text-zinc-400">{r.name}</code>
            <Pill cls={STATUS_PILL.deprecated}>{r.status}</Pill>
            <span className="text-[10px] text-zinc-500 leading-relaxed flex-1 min-w-[200px]">{r.description}</span>
        </div>
    );
}

function PointerCard({ title, chips, body, testId }) {
    return (
        <div className="border border-zinc-800 rounded-lg p-3 bg-black/30" data-testid={testId}>
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <span className="font-display font-extrabold text-xs uppercase text-white">{title}</span>
                {chips.map((c) => (
                    <span key={c} className={`pill text-[9px] font-bold uppercase border ${FORMAT_CHIPS[c].cls}`}>{FORMAT_CHIPS[c].label}</span>
                ))}
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">{body}</p>
        </div>
    );
}

// iter87.193.4 — Odds API force-sync tool card. Mirrors the Vendor
// Registry button (which lives under /platform Commons Console → Vendors)
// but ships the same one-click affordance into Product Ops → Tools so
// tenant site admins can trigger the manual poller from /admin on beta
// without needing access to the platform-scoped console.
// iter87.465.6 (user-requested, "where is the CFBD poller sync on
// Tools?") — CFBD's own vendor section, mirroring the Playoffs odds
// card above. Capped server-side at 1 real call/day (iter87.465.5) —
// this button is safe to click repeatedly, it just re-serves cache
// once already refreshed today.
function CfbdResyncCard() {
    const [syncing, setSyncing] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const onSync = async () => {
        setSyncing(true);
        try {
            const r = await api.cfbdResyncManual();
            setLastResult(r);
            if (r.stale_non_production) {
                toast.info(`Preview/beta only serve cache — ${r.participant_count} team(s) from last production refresh.`);
            } else if (r.cache_hit) {
                toast.success(`Already fresh (last refreshed ${Math.round((r.cache_age_seconds || 0) / 3600)}h ago) · ${r.participant_count} team(s)`);
            } else {
                toast.success(`CFBD resynced · ${r.participant_count} team(s) in the field`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "CFBD resync failed");
        }
        setSyncing(false);
    };
    return (
        <div className="border border-orange-900/50 rounded-lg p-3 bg-black/30" data-testid="cfbd-resync-tool-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-display font-extrabold text-xs uppercase text-white mb-1">Force resync CFP bracket now</div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                        CFB Playoffs team pool (the 12-team field + seeds) from CollegeFootballData.com. Production-only, capped at 1 real call/day — clicking this again today just re-confirms the cache, no extra call.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="cfbd-resync-tool-btn"
                    disabled={syncing}
                    onClick={onSync}
                    className="pill text-[10px] uppercase font-bold border-orange-500 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20 hover:brightness-110 disabled:opacity-60 disabled:cursor-wait transition shrink-0"
                    title="Manually refresh the CFP bracket from CollegeFootballData.com. Production-only; capped at 1 real call/day regardless of how many times this is clicked."
                >
                    {syncing ? "syncing…" : "sync now"}
                </button>
            </div>
            {lastResult && (
                <div className="mt-2 text-[10px] text-zinc-400" data-testid="cfbd-resync-tool-last">
                    Last run: {lastResult.participant_count} team(s) · {lastResult.cache_hit ? "served from cache" : "fresh pull"}
                    {lastResult.stale_non_production ? " (non-production, no live call made)" : ""}
                </div>
            )}
        </div>
    );
}

function OddsForceSyncCard() {
    const [syncing, setSyncing] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const onSync = async () => {
        setSyncing(true);
        try {
            const r = await api.oddsSpreadSyncManual();
            const remaining = r.results?.[r.results.length - 1]?.x_requests_remaining ?? "?";
            setLastResult({
                total_matched: r.total_matched ?? 0,
                sports_polled: r.sports_polled ?? 0,
                remaining,
                at: new Date().toISOString(),
            });
            toast.success(
                `Odds sync: ${r.total_matched ?? 0} rows written across ${r.sports_polled ?? 0} sports · ${remaining}/500 requests remaining`
            );
        } catch (err) {
            toast.error(err.response?.data?.detail || "Odds sync failed");
        }
        setSyncing(false);
    };
    return (
        <div className="border border-cyan-900/50 rounded-lg p-3 bg-black/30" data-testid="odds-force-sync-tool-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-display font-extrabold text-xs uppercase text-white mb-1">Force sync spread lines now</div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                        Bypasses the auto-scheduler and runs the bulk poller across every active sport (NFL, CFB, EPL, La Liga, Champions League). Writes fresh spread lines onto scheduled matches and back-fills any locked-in picks that were awaiting a first line.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="odds-force-sync-tool-btn"
                    disabled={syncing}
                    onClick={onSync}
                    className="pill text-[10px] uppercase font-bold border-cyan-500 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 hover:brightness-110 disabled:opacity-60 disabled:cursor-wait transition shrink-0"
                    title="Manually trigger the Odds API bulk poller. Bypasses the scheduler window. Consumes ~5-6 upstream calls (one per active sport) from the 500/month budget."
                >
                    {syncing ? "syncing…" : "sync now"}
                </button>
            </div>
            {lastResult && (
                <div className="mt-2 text-[10px] text-zinc-400" data-testid="odds-force-sync-tool-last">
                    Last run: {lastResult.total_matched} rows across {lastResult.sports_polled} sports · {lastResult.remaining}/500 requests remaining
                </div>
            )}
        </div>
    );
}

// iter87.465.3 (user-requested, "odds api ok to have 1 button") —
// Playoffs (NFL + CFB) odds are a separate sync path from the Weekly
// card above (different cache key, different poller, now gated to
// production-only per iter87.465.2) — one combined button covers
// both playoffs sports rather than two per-sport buttons, same
// one-button-per-vendor-path convention as the Weekly card.
function OddsPlayoffsForceSyncCard() {
    const [syncing, setSyncing] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const onSync = async () => {
        setSyncing(true);
        try {
            const r = await api.oddsPlayoffsSyncManual();
            const remaining = r.budget?.x_requests_remaining ?? "?";
            setLastResult({
                tournaments_synced: r.tournaments_synced ?? 0,
                remaining,
            });
            toast.success(
                `Playoffs odds sync: ${r.tournaments_synced ?? 0} tournament(s) synced · ${remaining}/500 requests remaining`
            );
        } catch (err) {
            toast.error(err.response?.data?.detail || "Playoffs odds sync failed");
        }
        setSyncing(false);
    };
    return (
        <div className="border border-cyan-900/50 rounded-lg p-3 bg-black/30 mt-2" data-testid="odds-playoffs-force-sync-tool-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-display font-extrabold text-xs uppercase text-white mb-1">Force sync Playoffs odds now</div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                        NFL + CFB + MLS Playoffs spread (Asian handicap for MLS) and Over/Under lines (Playoff Predictor). Separate from the Weekly sync above — production-only auto-poller, so use this to test on preview/beta without flipping any environment flags.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="odds-playoffs-force-sync-tool-btn"
                    disabled={syncing}
                    onClick={onSync}
                    className="pill text-[10px] uppercase font-bold border-cyan-500 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 hover:brightness-110 disabled:opacity-60 disabled:cursor-wait transition shrink-0"
                    title="Manually trigger the NFL/CFB/MLS Playoffs odds sync for every active tournament. Bypasses the production-only gate for this one click."
                >
                    {syncing ? "syncing…" : "sync now"}
                </button>
            </div>
            {lastResult && (
                <div className="mt-2 text-[10px] text-zinc-400" data-testid="odds-playoffs-force-sync-tool-last">
                    Last run: {lastResult.tournaments_synced} tournament(s) synced · {lastResult.remaining}/500 requests remaining
                </div>
            )}
        </div>
    );
}

// iter87.201.3 — Stale Unclosed Games: lists tournaments whose season
// has fully unwound (last game's kickoff passed) but still has games
// never marked played. Mirrors Drift Watch/Orphan Picks' "list + act"
// pattern: each game gets an optional score entry + a Mark Complete
// button (manual today; game_health_registry.md #19 flags the eventual
// auto-close as backlog).
function MarkCompleteRow({ game, onDone }) {
    const [scoreA, setScoreA] = useState("");
    const [scoreB, setScoreB] = useState("");
    const [busy, setBusy] = useState(false);
    const label = `${game.home_label || game.home_name || "?"} vs ${game.away_label || game.away_name || "?"}`;
    const onMark = async () => {
        setBusy(true);
        try {
            const payload = { schedule_id: game.id };
            if (scoreA !== "" && scoreB !== "") {
                payload.score_a = Number(scoreA);
                payload.score_b = Number(scoreB);
            }
            await api.gameHealthMarkGameComplete(payload);
            toast.success(`Marked complete: ${label}`);
            onDone(game.id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Mark complete failed");
        }
        setBusy(false);
    };
    // iter87.532 — MLB/NBA/UCL games live nested inside the tournament
    // doc, not a scheduled_matches row, so there's no `schedule_id` for
    // Mark Complete to patch here (it would just 404). These three are
    // read-only in this list — use "mark tournament complete" above
    // once the season is actually over.
    if (game.nested_source) {
        return (
            <div className="flex items-center gap-2 flex-wrap border border-zinc-800 rounded p-2 text-[11px] bg-black/30" data-testid={`stale-unclosed-game-${game.id}`}>
                <div className="flex-1 min-w-[200px]">
                    <div className="font-bold text-white">{label}</div>
                    <div className="text-[9px] text-zinc-600">
                        {game.round_label ? `${game.round_label} · ` : ""}{(game.scheduled_at || "").slice(0, 16).replace("T", " ")}
                    </div>
                </div>
                <span className="text-[9px] text-zinc-500 italic" data-testid={`stale-unclosed-nested-note-${game.id}`}>
                    synced from the live schedule — use "mark tournament complete" above once the season is over
                </span>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2 flex-wrap border border-zinc-800 rounded p-2 text-[11px] bg-black/30" data-testid={`stale-unclosed-game-${game.id}`}>
            <div className="flex-1 min-w-[200px]">
                <div className="font-bold text-white">{label}</div>
                <div className="text-[9px] text-zinc-600">
                    {game.round_label ? `${game.round_label} · ` : ""}{(game.scheduled_at || "").slice(0, 16).replace("T", " ")}
                </div>
            </div>
            <input type="number" placeholder="home" value={scoreA} onChange={(e) => setScoreA(e.target.value)}
                data-testid={`stale-unclosed-score-a-${game.id}`}
                className="w-16 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-1 text-[10px] text-white" />
            <input type="number" placeholder="away" value={scoreB} onChange={(e) => setScoreB(e.target.value)}
                data-testid={`stale-unclosed-score-b-${game.id}`}
                className="w-16 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-1 text-[10px] text-white" />
            <button type="button" disabled={busy} onClick={onMark}
                data-testid={`stale-unclosed-mark-complete-${game.id}`}
                className="brutal-btn text-[9px] py-1 px-2 bg-emerald-700 text-white">
                {busy ? "marking…" : "mark complete"}
            </button>
        </div>
    );
}

// iter87.205 — Free vs Pro+ tier pill. `tier` comes pre-computed from
// the backend scan (batch-loaded effective_tier, same pattern as
// site_admin/leagues.py's overview cards).
function TierPill({ tier, tournamentId }) {
    const isPro = tier === "pro_plus";
    return (
        <span
            className={`pill text-[9px] font-bold uppercase border ${
                isPro ? "border-cyber/60 text-cyber bg-cyber/10" : "border-zinc-700 text-zinc-400 bg-zinc-900/40"
            }`}
            data-testid={`stale-unclosed-tier-pill-${tournamentId}-${isPro ? "pro_plus" : "free"}`}
        >
            {isPro ? "Pro+" : "Free"}
        </span>
    );
}

function daysSince(iso) {
    if (!iso) return null;
    return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

// iter87.205 — One row per open TOURNAMENT (was one row per unplayed
// game) — the "mark tournament complete" button is the primary action
// (reuses the same completion logic a league admin already has); the
// per-game score-entry rows (MarkCompleteRow, iter87.201.3) are tucked
// behind "view games" since that's a secondary drill-down, not the
// headline info a site admin scanning this list needs.
function StaleUnclosedTournamentRow({ tournament, selected, onToggleSelect, onClosed }) {
    const [expanded, setExpanded] = useState(false);
    const [closing, setClosing] = useState(false);
    const [games, setGames] = useState(tournament.unplayed_games);
    const days = daysSince(tournament.last_kickoff);

    const closeTournament = async () => {
        setClosing(true);
        try {
            await api.gameHealthCloseTournament({ tournament_id: tournament.id });
            toast.success(`Closed: ${tournament.name || tournament.id}`);
            onClosed(tournament.id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Close failed");
        }
        setClosing(false);
    };

    return (
        <div className="border border-zinc-800 rounded p-3 bg-black/30 space-y-2" data-testid={`stale-unclosed-tournament-${tournament.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelect(tournament.id)}
                    data-testid={`stale-unclosed-select-${tournament.id}`}
                    className="mt-1 accent-cyber"
                />
                <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold text-white">
                            {tournament.league_name || "unknown league"} › {tournament.name || tournament.id}
                        </span>
                        <TierPill tier={tournament.tier} tournamentId={tournament.id} />
                        <span className="pill text-[9px] font-bold uppercase border-zinc-700 text-zinc-500">
                            {(tournament.template || "").replace("_api", "").replace(/_/g, " ")}
                        </span>
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">
                        Last game {days != null ? `${days}d ago` : "unknown"} · {games.length} unplayed game(s)
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setExpanded((v) => !v)}
                        data-testid={`stale-unclosed-toggle-games-${tournament.id}`}
                        className="brutal-btn-ghost text-[9px] py-1 px-2 flex items-center gap-1">
                        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        view games
                    </button>
                    <button type="button" disabled={closing} onClick={closeTournament}
                        data-testid={`stale-unclosed-close-tournament-${tournament.id}`}
                        className="brutal-btn text-[9px] py-1 px-2 bg-emerald-700 text-white disabled:opacity-40">
                        {closing ? "closing…" : "mark tournament complete"}
                    </button>
                </div>
            </div>
            {expanded && (
                <div className="space-y-1.5 pl-6">
                    {games.map((g) => (
                        <MarkCompleteRow key={g.id} game={g}
                            onDone={(gid) => setGames((prev) => prev.filter((x) => x.id !== gid))} />
                    ))}
                </div>
            )}
        </div>
    );
}

function StaleUnclosedGamesCard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    // iter87.205 — Free vs Pro+ tier filter, so a site admin can e.g.
    // filter to "Free" and close those aggressively right after the
    // 2-day grace, while reviewing Pro+ (paying-customer) tournaments
    // individually before closing.
    const [tierFilter, setTierFilter] = useState("all"); // all | free | pro_plus
    const [selected, setSelected] = useState(new Set());
    const [bulkClosing, setBulkClosing] = useState(false);
    const { confirm, hostElement } = useBrutalConfirm();

    const load = () => {
        api.gameHealthStaleUnclosedGames()
            .then((d) => setData(d.tournaments))
            .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load"));
    };
    useEffect(() => { load(); }, []);

    const removeTournament = (tournamentId) => {
        setData((prev) => (prev || []).filter((t) => t.id !== tournamentId));
        setSelected((prev) => {
            const next = new Set(prev);
            next.delete(tournamentId);
            return next;
        });
    };

    const filtered = (data || []).filter((t) => tierFilter === "all" || t.tier === tierFilter);
    const allSelected = filtered.length > 0 && filtered.every((t) => selected.has(t.id));

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleSelectAll = () => {
        setSelected(allSelected ? new Set() : new Set(filtered.map((t) => t.id)));
    };

    const closeSelected = async () => {
        const ids = filtered.filter((t) => selected.has(t.id)).map((t) => t.id);
        if (ids.length === 0) return;
        const ok = await confirm({
            title: `Close ${ids.length} tournament(s)?`,
            body: "Each is marked complete and locked read-only — same effect as a league admin closing it manually. This can't be undone from here.",
            confirmLabel: `Close ${ids.length}`,
            destructive: true,
        });
        if (!ok) return;
        setBulkClosing(true);
        try {
            const r = await api.gameHealthBulkCloseTournaments({ tournament_ids: ids });
            if (r.closed?.length) {
                toast.success(`Closed ${r.closed.length} tournament(s)`);
                r.closed.forEach(removeTournament);
            }
            if (r.errors?.length) {
                toast.error(`${r.errors.length} tournament(s) failed to close`);
                console.error("bulk-close errors", r.errors);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk close failed");
        }
        setBulkClosing(false);
    };

    const refreshBtn = (
        <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); load(); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); load(); } }}
            data-testid="stale-unclosed-refresh-btn"
            className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
        >
            <RefreshCw className="w-3 h-3" /> Re-scan
        </span>
    );

    if (error) return <div className="text-xs text-rose-300" data-testid="stale-unclosed-error">{error}</div>;
    if (!data) return <div className="text-xs text-zinc-500" data-testid="stale-unclosed-loading">Scanning finished tournaments…</div>;
    return (
        <CollapsibleCard
            testId="stale-unclosed-games-card"
            icon={Gamepad2}
            title="Stale unclosed tournaments"
            badge={data.length === 0 ? "No stuck tournaments" : `${data.length} open`}
            badgeClass={data.length === 0
                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                : "bg-amber-500/15 text-amber-300 border-amber-500/40"}
            verdict={data.length === 0 ? "clear" : "warn"}
            headerExtra={refreshBtn}
            defaultOpen
        >
            {hostElement}
            {data.length === 0 ? (
                <div className="text-[11px] text-emerald-300 border border-emerald-900/50 bg-emerald-950/10 rounded p-3" data-testid="stale-unclosed-empty">
                    No stuck tournaments — every finished season/bracket is fully closed out.
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-2 flex-wrap">
                        {["all", "free", "pro_plus"].map((k) => {
                            const n = k === "all" ? data.length : data.filter((t) => t.tier === k).length;
                            return (
                                <button key={k} type="button" onClick={() => setTierFilter(k)}
                                    data-testid={`stale-unclosed-filter-${k}`}
                                    className={`pill text-[9px] font-bold uppercase border ${
                                        tierFilter === k ? "border-cyber text-cyber bg-cyber/10" : "border-zinc-700 text-zinc-500"
                                    }`}>
                                    {(k === "all" ? "All" : k === "free" ? "Free" : "Pro+")} ({n})
                                </button>
                            );
                        })}
                        <div className="flex-1" />
                        <label className="flex items-center gap-1.5 text-[10px] text-zinc-400 cursor-pointer">
                            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                                data-testid="stale-unclosed-select-all" className="accent-cyber" />
                            select all
                        </label>
                        <button type="button" disabled={selected.size === 0 || bulkClosing} onClick={closeSelected}
                            data-testid="stale-unclosed-close-selected"
                            className="brutal-btn text-[9px] py-1.5 px-3 bg-emerald-700 text-white disabled:opacity-40">
                            {bulkClosing ? "closing…" : `Close Selected (${selected.size})`}
                        </button>
                    </div>
                    {filtered.length === 0 ? (
                        <div className="text-[11px] text-zinc-500" data-testid="stale-unclosed-filter-empty">
                            No {tierFilter === "pro_plus" ? "Pro+" : "Free"} tournaments in this list.
                        </div>
                    ) : filtered.map((t) => (
                        <StaleUnclosedTournamentRow key={t.id} tournament={t}
                            selected={selected.has(t.id)} onToggleSelect={toggleSelect}
                            onClosed={removeTournament} />
                    ))}
                </>
            )}
        </CollapsibleCard>
    );
}

// iter87.285 (operator-flagged backlog) — Orphaned Schedule Rows:
// `scheduled_matches` rows left behind by tournament deletions that
// predate `cascade_delete_tournament`. Preview-then-confirm, same
// destructive-action shape as Stale Unclosed's bulk close.
function OrphanedScheduleRowsCard() {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const { confirm, hostElement } = useBrutalConfirm();

    const loadPreview = () => {
        setBusy(true);
        api.gameHealthOrphanedScheduleRowsPreview()
            .then((d) => setPreview(d))
            .catch((e) => toast.error(e?.response?.data?.detail || "Preview failed"))
            .finally(() => setBusy(false));
    };
    useEffect(() => { loadPreview(); }, []);

    const onCleanup = async () => {
        if (!preview?.orphaned_rows) return;
        const ok = await confirm({
            title: `Delete ${preview.orphaned_rows} orphaned row(s)?`,
            body: `These belong to ${preview.orphaned_tournaments} tournament(s) that no longer exist. Deletion is permanent — this only removes leftover schedule rows, never a live tournament.`,
            confirmLabel: `Delete ${preview.orphaned_rows}`,
            destructive: true,
        });
        if (!ok) return;
        setBusy(true);
        try {
            const r = await api.gameHealthOrphanedScheduleRowsCleanup();
            setLastResult(r);
            toast.success(`Deleted ${r.deleted} orphaned row(s) across ${r.orphaned_tournaments} tournament(s)`);
            setPreview({ orphaned_tournaments: 0, orphaned_rows: 0, deleted: 0 });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Cleanup failed");
        }
        setBusy(false);
    };

    const clean = preview && preview.orphaned_rows === 0;
    return (
        <div className="border border-cyan-900/50 rounded-lg p-3 bg-black/30" data-testid="orphaned-schedule-rows-tool-card">
            {hostElement}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-display font-extrabold text-xs uppercase text-white mb-1">Orphaned schedule rows</div>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                        Scans every `scheduled_matches` row for a `tournament_id` that no longer resolves to a real tournament (left over from deletions that predate the current cascade-delete path), then permanently removes them.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="orphaned-schedule-rows-refresh-btn"
                    disabled={busy}
                    onClick={loadPreview}
                    title="Re-scan for orphaned rows"
                    className="pill text-[10px] uppercase font-bold border-zinc-700 bg-black/30 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500 disabled:opacity-40 transition shrink-0"
                >
                    <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
                </button>
                <button
                    type="button"
                    data-testid="orphaned-schedule-rows-cleanup-btn"
                    disabled={busy || !preview?.orphaned_rows}
                    onClick={onCleanup}
                    className="pill text-[10px] uppercase font-bold border-cyan-500 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition shrink-0"
                >
                    {busy ? "working…" : "delete orphaned rows"}
                </button>
            </div>
            {preview && (
                <div
                    className={`mt-2 text-[10px] ${clean ? "text-emerald-300" : "text-amber-300"}`}
                    data-testid="orphaned-schedule-rows-preview"
                >
                    {clean
                        ? "Clean — no orphaned schedule rows found."
                        : `${preview.orphaned_rows} row(s) across ${preview.orphaned_tournaments} deleted tournament(s) ready to delete.`}
                </div>
            )}
            {lastResult && (
                <div className="mt-1 text-[10px] text-zinc-500" data-testid="orphaned-schedule-rows-last">
                    Last run: deleted {lastResult.deleted} row(s) across {lastResult.orphaned_tournaments} tournament(s)
                </div>
            )}
        </div>
    );
}

function ToolsView({ registry }) {
    const [healers, setHealers] = useState(null);
    useEffect(() => {
        api.gameHealthHealers().then((d) => setHealers(d.healers)).catch(() => setHealers([]));
    }, []);
    const registryByName = Object.fromEntries((registry?.rows || []).map((r) => [r.name, r]));
    const healersByName = Object.fromEntries((healers || []).map((h) => [h.name, h]));
    // iter87.201.3 fix — was filtered to upstream==="espn" only, which
    // silently dropped `ratchet_audit` (upstream="cross", deprecated
    // iter87.57) from the audit trail entirely. Deprecated status is the
    // right filter, not upstream, since retirements span vendors.
    const retiredRows = (registry?.rows || []).filter((r) => r.status === "deprecated");

    return (
        <div className="space-y-6" data-testid="game-ops-tools-view">
            <p className="text-[11px] text-zinc-500">
                Grouped by upstream vendor. Every card names its registry healer, describes the exact
                data flow (where it reads from → what it writes), whether an auto-healer already covers
                it, and real run evidence (7d/30d) where instrumented. Detector-found problems land in
                <span className="text-zinc-300 font-bold"> Issues</span> with a one-click healer first; come here for manual runs.
            </p>

            <VendorGroup vendorKey="api-football" count={3} testId="game-ops-tools-api-football"
                blurb="Soccer knockout formats (World Cup KO style brackets) — teams, scores and winner flags sync from API-Football.">
                <ToolWrap formats={["soccer_ko"]} vendor="api_football" name="recompute_team_stats"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-team-stats"
                    useWhen="a match clearly finished (bracket / The Ride moved) but a team's leaderboard rows stay stuck at 0.">
                    <SiteAdminRecomputeTeamStatsCard />
                </ToolWrap>
                <ToolWrap formats={["soccer_ko"]} vendor="api_football" name="repull_upstream_winners"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-repull-winners"
                    useWhen="a team advanced upstream (e.g. penalty shootout) but the local winner disagrees.">
                    <SiteAdminRepullUpstreamWinnersCard />
                </ToolWrap>
                <ToolWrap formats={["soccer_ko"]} vendor="api_football" name="api_football_poller_cadence"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-api-football-poller"
                    useWhen="you want to tune how aggressively the poller refreshes soccer KO brackets, or quota is running tight. iter87.389: moved here from Templates → Pollers per the one-canonical-home rule.">
                    <SiteAdminPollerCadenceCard />
                </ToolWrap>
            </VendorGroup>

            <VendorGroup vendorKey="the-odds-api" count={2} testId="game-ops-tools-odds"
                blurb="Pre-game spread lines across NFL, CFB, EPL, La Liga and Champions League (Weekly), plus spread + Over/Under for NFL, CFB and MLS Playoffs (Playoff Predictor) — two separate cache keys/pollers. Auto-poller runs eleven UTC windows/week per sport (Weekly) or production-only (Playoffs); use a manual sync only when awaiting a window or right after a fresh deploy.">
                <ToolWrap formats={["odds_api"]} vendor="the_odds_api" name="odds_force_sync"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-odds-force-sync"
                    useWhen="a game shows LINE TBD, a locked-in pick shows AWAITING FIRST LINE, or you just redeployed and want spreads to populate without waiting for the next scheduler window.">
                    <OddsForceSyncCard />
                </ToolWrap>
                <ToolWrap formats={["odds_api"]} vendor="the_odds_api" name="odds_playoffs_force_sync"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-odds-playoffs-force-sync"
                    useWhen="an NFL/CFB Playoffs game shows no spread/total yet, or an MLS Playoff Predictor game (Conf Semis+) shows no line yet, on preview or beta, where the auto-poller is production-only (iter87.465.2).">
                    <OddsPlayoffsForceSyncCard />
                </ToolWrap>
            </VendorGroup>

            <VendorGroup vendorKey="highlightly" count={2} testId="game-ops-tools-highlightly"
                blurb="MLB & NBA Playoffs — teams, series scores and bracket advancement sync from Highlightly. Settings, not fixes: tune poll cadence or trigger a manual Run Now per sport. iter87.276: moved here from Templates → Pollers (was card-only, un-registered) per the one-canonical-home rule.">
                <ToolWrap formats={["mlb"]} vendor="highlightly" name="mlb_poller_cadence"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-mlb-poller"
                    useWhen="an MLB Playoffs tournament's bracket/series looks stale, or you want to force a fresh Highlightly pull right now instead of waiting for the next poller wake.">
                    <MlbPollerCadenceCard />
                </ToolWrap>
                <ToolWrap formats={["nba"]} vendor="highlightly" name="nba_poller_cadence"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-nba-poller"
                    useWhen="an NBA Playoffs tournament's bracket/series looks stale, or you want to force a fresh Highlightly pull right now instead of waiting for the next poller wake.">
                    <NbaPollerCadenceCard />
                </ToolWrap>
            </VendorGroup>

            <VendorGroup vendorKey="api-nfl" count={1} testId="game-ops-tools-api-nfl"
                blurb="NFL season + playoffs — standings, weekly results and spreads sync from API-NFL (API-Sports). Settings only (no manual Run Now) — the sync itself is fully automatic. iter87.389: moved here from Templates → Pollers per the one-canonical-home rule.">
                <ToolWrap formats={["football_season"]} vendor="api_nfl" name="nfl_poller_cadence"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-nfl-poller"
                    useWhen="an NFL season tournament's standings or weekly results look stale, or you want to tune cadence/quota ahead of a busy game day.">
                    <NflPollerCadenceCard />
                </ToolWrap>
            </VendorGroup>

            <VendorGroup vendorKey="slash-golf" count={1} testId="game-ops-tools-slash-golf"
                blurb="Golf tournaments — leaderboard + field sync from Slash Golf (RapidAPI), with an automatic final-round (Sunday) cadence boost. Settings only (no manual Run Now) — the sync itself is fully automatic. iter87.389: moved here from Templates → Pollers per the one-canonical-home rule.">
                <ToolWrap formats={["golf"]} vendor="slash_golf" name="golf_poller_cadence"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-golf-poller"
                    useWhen="a golf leaderboard looks stale, RapidAPI quota is running tight, or you want to check/adjust the final-round boost.">
                    <SlashGolfPollerCadenceCard />
                </ToolWrap>
            </VendorGroup>

            <VendorGroup vendorKey="cfbd" count={1} testId="game-ops-tools-cfbd"
                blurb="CFB Playoffs 12-team field + seeds — sync from CollegeFootballData.com. Production-only, capped at 1 real call/day (iter87.465.5); use this to re-check the field on preview/beta without flipping any environment flags.">
                <ToolWrap formats={["cfb_playoffs"]} vendor="cfbd" name="cfbd_resync"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-cfbd-resync"
                    useWhen="the CFP bracket hasn't materialized yet on preview/beta, or you want to confirm today's field without waiting for the daily production pull.">
                    <CfbdResyncCard />
                </ToolWrap>
            </VendorGroup>

            <VendorGroup vendorKey="cross" count={6} testId="game-ops-tools-cross"
                blurb="Upstream-agnostic — apply to any tournament regardless of vendor.">
                <ToolWrap formats={["any"]} vendor="any" name="scoring_drift_heal"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-scoring-drift"
                    useWhen="leaderboard totals disagree with the recorded match evidence.">
                    <ScoringDriftHealCard environment="preview" />
                </ToolWrap>
                <ToolWrap formats={["any"]} vendor="any" name="close_tournament"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-stale-unclosed"
                    useWhen="a template-based tournament's season/bracket has genuinely ended 2+ days ago but it's still open. Excludes custom AND ESPN-legacy (World Cup/March Madness/Football Playoffs) tournaments entirely — filter by Free/Pro+, select individually or bulk-close.">
                    <StaleUnclosedGamesCard />
                </ToolWrap>
                <ToolWrap formats={["any"]} vendor="any" name="drift_watch"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-drift-watch"
                    useWhen="a tournament shows duplicate match documents or suspicious data drift.">
                    <CollapsibleCard title="Drift Watch" subtitle="Tournaments with duplicate match docs" data-testid="game-ops-drift-watch-card">
                        <DriftWatchCardContent />
                    </CollapsibleCard>
                </ToolWrap>
                <ToolWrap formats={["any"]} vendor="any" name="orphaned_schedule_rows"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-orphaned-schedule-rows"
                    useWhen="scheduled_matches rows are left behind after a tournament is deleted through a path older than the current cascade-delete — pure housekeeping, no live tournament is ever touched.">
                    <OrphanedScheduleRowsCard />
                </ToolWrap>
                <ToolWrap formats={["any"]} vendor="any" name="orphan_picks_audit"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-orphan-picks"
                    useWhen="picks reference teams or players that no longer exist in the tournament.">
                    <OrphanPicksAuditCard environment="preview" />
                </ToolWrap>
                <ToolWrap formats={["any"]} vendor="any" name="bulk_cleanup"
                    registryByName={registryByName} healersByName={healersByName}
                    testId="game-ops-toolwrap-cleanup"
                    useWhen="stale abandoned rivalries (no draft, no matches, ≤1 member, 30+ days old) clutter the tenant.">
                    <SiteAdminCleanupCard />
                </ToolWrap>
            </VendorGroup>

            {retiredRows.length > 0 && (
                <VendorGroup vendorKey="retired" count={retiredRows.length} testId="game-ops-tools-retired"
                    blurb="Deprecated detectors/healers, kept as an audit trail per wf.governance.deprecation (never deleted) — endpoints retained for emergency use only, not exposed as buttons.">
                    {retiredRows.map((r) => <RetiredRow key={r.name} r={r} />)}
                </VendorGroup>
            )}

            <ToolGroup title="Looking for Soccer Season tools?" blurb="Explicitly: none exist — here's why, and where to reach instead. Golf and Football Season poller cadence now live in their own vendor groups above (Slash Golf / API-NFL)." testId="game-ops-tools-pointers">
                <PointerCard title="Soccer Season" chips={["soccer_season"]} testId="game-ops-pointer-season"
                    body="No season-specific tools — standings sync is automatic from API-Football. If a season leaderboard looks off, the cross-cutting tools above (Scoring Drift Heal, Drift Watch, Orphan Picks) are the right reach." />
            </ToolGroup>
        </div>
    );
}

// --------------------------------------------------------------- Catalog --

function CatalogView() {
    return (
        <div className="space-y-6" data-testid="game-ops-catalog-view">
            <ToolGroup title="Feature catalog" blurb="The product's canonical feature registry — ids, status, product home, vendor + doc joins. Source of truth: feature_registry.md." testId="game-ops-catalog-features">
                <FeatureCatalogCard defaultOpen />
            </ToolGroup>

            <ToolGroup title="UAT generator" blurb="Generate the per-iteration UAT runbook from the registry instead of hand-authoring it. Feeds Release Ops Phase 3." testId="game-ops-catalog-uat">
                <GenerateUatCard />
            </ToolGroup>
        </div>
    );
}

// ------------------------------------------------------------- Templates --
// iter86.31.4 — Tournament Templates promoted to its own sub-tab (moved
// out of Catalog). Home of the 3-axis model: shape (template) × source
// (vendor) × event binding (live competition).

function TemplatesView() {
    return (
        <div className="space-y-6" data-testid="game-ops-templates-view">
            <ToolGroup title="Event bindings" blurb="Which live Golf events the Golf templates may bind to — curated from the Slash Golf schedule. The only shape that needs curation; every other shape binds via a hardcoded vendor config." testId="game-ops-templates-bindings">
                <TemplateBindingsCard />
            </ToolGroup>

            <ToolGroup title="Vendor data cascade" blurb="Pushes fresh cached vendor data into existing rivalries for the templates whose event binding is hardcoded (World Cup, Premier League, La Liga, Champions League)." testId="game-ops-templates-cascade">
                <VendorDataCascadeCard />
            </ToolGroup>

            <ToolGroup title="Template governance" blurb="Platform-wide visibility (live / coming soon / hidden) + per-shape scoring baselines. Tenant availability lives on the Site Admin pane (/admin → Templates)." testId="game-ops-templates-governance">
                <SiteAdminTemplateVisibility />
                <TemplateScoringCard />
            </ToolGroup>

            <ToolGroup title="Draft pots / tiers" blurb="Re-rank each club season's tiers (baseline: last season's final table). Tiers drive Pot Pick'em pots and snake-draft order — changes apply to new tournaments only." testId="game-ops-templates-pots">
                <TemplatePotsCard />
            </ToolGroup>
        </div>
    );
}

// ------------------------------------------------------------------- Tab --

export default function GameOpsTab({ initialTarget }) {
    const [summary, setSummary] = useState(null);
    const [registry, setRegistry] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sub, setSub] = useState("health");

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const [s, r] = await Promise.all([api.gameHealthSummary(), api.gameHealthRegistry()]);
            setSummary(s);
            setRegistry(r);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // iter84.72 — Deep-link support: Release Ops sweep rows land the
    // operator on a specific sub-view (and optionally scroll to a card).
    useEffect(() => {
        if (!initialTarget?.sub) return;
        setSub(initialTarget.sub);
        if (initialTarget.anchor) {
            setTimeout(() => {
                document.querySelector(`[data-testid="${initialTarget.anchor}"]`)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 350);
        }
    }, [initialTarget]);

    const issueCount = summary?.issue_count ?? 0;
    const SUB_TABS = [
        { k: "health", label: "Health", icon: HeartPulse },
        { k: "issues", label: issueCount ? `Issues (${issueCount})` : "Issues", icon: ListChecks },
        { k: "healers", label: "Healers", icon: Stethoscope },
        { k: "catalog", label: "Catalog", icon: BookOpenCheck },
        { k: "templates", label: "Templates", icon: FileJson },
        { k: "tools", label: "Tools", icon: Wrench },
    ];

    return (
        <div className="space-y-4" data-testid="game-ops-tab">
            <div className="brutal-card p-4 border-2 border-emerald-900/40 bg-emerald-950/10">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Gamepad2 className="w-4 h-4 text-emerald-300" />
                            <h2 className="font-display font-extrabold text-lg uppercase tracking-wide text-white">
                                Product Ops
                            </h2>
                            <WhatsThisButton
                                testId="product-ops-whats-this"
                                title="Product Ops"
                                what="The operating room for the game itself. Detectors continuously scan live data (orphan picks, scoring drift, duplicate matches, tied KOs, stale syncs); open findings queue in Issues, each with a paired one-click healer; manual tools live in Tools."
                                lookFor="Overall posture OK. If a domain shows findings, open Issues and run the paired healer — most problems here are one click to fix."
                                notFor="Deploy decisions — Release Ops reads this health as a rollup, but shipping gates live there, not here."
                            />
                            {summary && (
                                <Pill cls={POSTURE_PILL[summary.overall] || POSTURE_PILL.warn} testId="game-ops-overall-posture">
                                    {summary.overall}
                                </Pill>
                            )}
                        </div>
                        <div className="text-xs text-zinc-400">
                            Health-first game operations: detectors roll up per domain, open findings queue in
                            Issues with their paired healer, manual tools live in Tools. Taxonomy:
                            Docs → GAME_HEALTH_REGISTRY.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide bg-zinc-900 border border-zinc-700 rounded hover:text-white"
                        data-testid="game-ops-refresh-btn"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            <SubTabs items={SUB_TABS} active={sub} onChange={setSub} testPrefix="game-ops-subtab" />

            {error && (
                <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-800 rounded p-3" data-testid="game-ops-error">
                    {error}
                </div>
            )}

            {sub === "health" && (
                <HealthView summary={summary} registry={registry} onOpenIssues={() => setSub("issues")} />
            )}
            {sub === "issues" && (
                <IssuesView summary={summary} reload={load} onOpenTools={() => setSub("tools")} />
            )}
            {sub === "catalog" && <CatalogView />}
            {sub === "templates" && <TemplatesView />}
            {sub === "healers" && <HealersView />}
            {sub === "tools" && <ToolsView registry={registry} />}
        </div>
    );
}
